import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { OverflowTooltip } from '@/components/ui/overflow-tooltip';
import type { ProvidedJobPostsResponse } from '../types/ProvidedJobPost';
import usePdf from '../hooks/usePdf';
import { Tag, type TagColor } from '@/components/Tag';
import type { CustomTagColor } from '../../custom_tags/hooks/useCustomTags';
import TagEditDialog from './TagEditDialog';
import {
  MdShare,
  MdOpenInNew,
  MdDescription,
  MdLink,
  MdApartment,
  MdWork,
  MdLocationOn,
  MdBadge,
} from 'react-icons/md';
import { Button } from '@/components/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import JobPostStatusBadge from './JobPostStatusBadge';
import { Link } from '@tanstack/react-router';
import MemoDisplay from './MemoDisplay';
import ShareContentDialog from './ShareContentDialog';
import { ImDatabase } from 'react-icons/im';

type Props = {
  jobPost: ProvidedJobPostsResponse | undefined;
  counterCache: ProvidedJobPostsResponse['counter_cache'] | undefined;
};

export default function ProvidedJobPostsHeader({ jobPost, counterCache }: Props) {
  const shareUrl = jobPost?.urls?.open || '';
  const [isSharePopoverOpen, setIsSharePopoverOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const shareContent = useMemo(() => {
    return `${jobPost?.company.name}\n${jobPost?.title}\n${shareUrl}`;
  }, [shareUrl]);
  const { downloadPdf } = usePdf();

  return (
    <div className="bg-white">
      <div className="flex flex-col gap-3 px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-4">
            <h1 className="block truncate text-xl font-bold">
              {jobPost?.title || '読み込み中...'}
            </h1>
            <div className="mr-4 shrink-0">
              <JobPostStatusBadge
                id={jobPost?.id ?? 0}
                status={jobPost?.status ?? ''}
                created_at={jobPost?.created_at}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            {jobPost?.outer_job_post && (
              <Button color="tertiary" size="sm" asChild>
                <a
                  href={jobPost.outer_job_post.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1"
                >
                  {jobPost.outer_job_post.job_source_display_name ||
                    jobPost.outer_job_post.job_source_name ||
                    '取り込み元を見る'}
                  <MdOpenInNew className="size-icon-xxs" />
                </a>
              </Button>
            )}
            {/* url が null（jobmiru で host 未解決の経路など / #3010）のときはリンク非表示 */}
            {jobPost?.shared_job_post?.url && (
              <Button color="tertiary" size="sm" asChild>
                <a
                  href={jobPost.shared_job_post.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1"
                >
                  {jobPost.shared_job_post.provider_display_name || '取り込み元を見る'}
                  <MdOpenInNew className="size-icon-xxs" />
                </a>
              </Button>
            )}

            <Popover open={isSharePopoverOpen} onOpenChange={setIsSharePopoverOpen}>
              <PopoverTrigger asChild>
                <Button color="secondary" size="sm" iconOnly>
                  <MdShare className="size-icon-xxs" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[130px] rounded-sm p-0 shadow-layer3">
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSharePopoverOpen(false);
                      if (!jobPost?.is_available) {
                        alert('公開中の求人票のみ共有できます。公開してください');
                      } else {
                        setIsShareDialogOpen(true);
                      }
                    }}
                    className="tex flex h-xxl items-center gap-xs px-md py-xs text-body-sm hover:bg-gray"
                  >
                    <MdLink className="size-icon-xxs" />
                    WEB共有
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSharePopoverOpen(false);
                      if (jobPost?.id) {
                        downloadPdf(jobPost.id);
                      }
                    }}
                    className="flex h-xxl items-center gap-xs px-md py-xs text-body-sm text-main hover:bg-gray"
                  >
                    <MdDescription className="size-icon-xxs" />
                    PDF共有
                  </button>
                </div>
              </PopoverContent>
            </Popover>
            <ShareContentDialog
              title="求人票のURLをコピーする"
              shareText={shareContent}
              open={isShareDialogOpen}
              onOpenChange={setIsShareDialogOpen}
            />
          </div>
        </div>

        <div className="flex items-start justify-between gap-1">
          <div className="flex flex-wrap gap-4">
            {jobPost?.company && (
              <div className="flex items-center gap-2 border-r border-solid border-gray-300 pr-4">
                <MdApartment size={20} />
                <Link
                  to="/agent/companies/$companiesId"
                  params={{ companiesId: String(jobPost.company.id) }}
                  className="text-xs hover:underline"
                >
                  {jobPost.company.name}
                </Link>
              </div>
            )}
            {jobPost?.job_position_name && (
              <div className="flex items-center gap-2 border-r border-solid border-gray-300 pr-4">
                <MdWork size={20} className="shrink-0" />
                <OverflowTooltip
                  text={jobPost.job_position_name}
                  className="max-w-[200px] text-xs"
                />
              </div>
            )}
            {jobPost?.salary_info && (
              <div className="flex items-center gap-2 border-r border-solid border-gray-300 pr-4">
                <ImDatabase size={16} />
                <p className="text-xs">{jobPost.salary_info}</p>
              </div>
            )}
            {jobPost?.employment_type_text && (
              <div className="flex items-center gap-2 border-r border-solid border-gray-300 pr-4">
                <MdBadge size={20} />
                <p className="text-xs">{jobPost.employment_type_text}</p>
              </div>
            )}
            {jobPost?.working_location && (
              <div className="flex items-center gap-2">
                <MdLocationOn size={20} />
                <p className="text-xs">{jobPost.working_location}</p>
              </div>
            )}
            {jobPost?.memo && <MemoDisplay memo={jobPost.memo} />}
          </div>
          {counterCache && (
            <div className="flex items-center gap-4">
              <Counter count={counterCache.count_of_suggest} label="提案" />
              <Counter count={counterCache.count_of_document_screening} label="書類選考" />
              <Counter count={counterCache.count_of_interview} label="選考中" />
              <Counter count={counterCache.count_of_offer} label="内定" />
              <Counter count={counterCache.count_of_close} label="内定承諾" />
            </div>
          )}
        </div>

        {jobPost && (
          <div className="flex gap-1">
            <div className="flex flex-wrap items-center gap-2">
              {jobPost.tags &&
                jobPost.tags.length !== 0 &&
                jobPost.tags.map((tag) => (
                  <Tag key={tag.id} color={tag.color_hex as TagColor} size="md">
                    {tag.name}
                  </Tag>
                ))}
              {jobPost.id && (
                <TagEditDialog
                  id={jobPost.id}
                  tags={
                    jobPost.tags?.map((tag) => ({
                      id: tag.id,
                      name: tag.name,
                      colorHex: tag.color_hex as CustomTagColor,
                    })) || []
                  }
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Counter({ count, label }: { count: number; label: string }) {
  return (
    <div className="flex h-[68px] w-[80px] flex-col items-center justify-center gap-1 rounded border border-solid border-border bg-white">
      <p className={clsx('text-base font-bold', count === 0 && 'text-border')}>{count}</p>
      <p className="text-xs font-bold">{label}</p>
    </div>
  );
}
